/**
 * `ParagraphIdExtension` — adds a stable `id` attribute to every
 * `paragraph` node in the doc schema and renders it as `data-paragraph-id`
 * on the rendered DOM.
 *
 * Why this exists:
 *   Wiki comments can be anchored to a specific paragraph via
 *   `wiki_comments.anchor_paragraph_id`. To scroll-to-anchor on thread
 *   click, the rendered paragraph DOM has to expose a stable lookup key.
 *   The id rides through the JSON save round-trip automatically (TipTap
 *   serializes node attributes into `editor.getJSON()`), so the next
 *   autosave-trigger flush persists it.
 *
 * Lifecycle / mutation rules:
 *   - The id is `crypto.randomUUID()` minted at paragraph-creation time.
 *     Once set it is never mutated — EXCEPT to break a duplicate (below);
 *     existing paragraphs keep their original id.
 *   - **Splitting a paragraph COPIES its attrs, id included.** ProseMirror's
 *     `splitBlock` carries the attrs onto the new block, so every Enter
 *     press produced a second paragraph with the same id, and the mount
 *     back-fill (which only fills NULLs) never repaired it — measured on
 *     the local wiki: one doc with 44 paragraphs sharing 8 ids, another
 *     with 7 paragraphs on 1 id. Inline comments resolve their anchor by
 *     paragraph id, so a duplicate sent the highlight to the FIRST
 *     paragraph carrying the id and a text search there — the selected
 *     word lit up somewhere else, or not at all. The plugin below walks
 *     the doc after every content change (and once on view init) and
 *     re-mints the id of every paragraph whose id was already seen
 *     earlier in the doc. The first occurrence keeps the id, so anchors
 *     on it survive; the replacement is DERIVED (`deriveDuplicateParagraphId`
 *     — a hash of the original id + the duplicate's ordinal), never random,
 *     so every client that opens the same unsaved duplicates computes the
 *     same repaired ids and an anchor captured in one tab resolves in another.
 *   - Back-filling existing paragraph nodes (loaded from a doc that
 *     pre-dates this extension) is the responsibility of the editor
 *     consumer — see `<MarkdownEditor>` / `<DocPage>` for the one-shot
 *     mount-time back-fill that walks `editor.state.doc.descendants` and
 *     dispatches a `setMeta('addToHistory', false)` transaction.
 *   - `parseHTML` reads `data-paragraph-id` so pasting back content
 *     produced by this editor round-trips the id; missing attribute
 *     defaults to null and gets back-filled on the next mount tick.
 *
 * Why `addGlobalAttributes` (not `Paragraph.extend(...)`):
 *   `@tiptap/extension-paragraph` is a transitive dep through StarterKit
 *   — extending it directly would break the dep-boundary convention the
 *   wikilink / slash-command extensions follow (they extend `Mention`,
 *   a direct dep, for the same reason). `Extension.create` with
 *   `addGlobalAttributes` is the first-party `@tiptap/core` API for
 *   widening a node's attribute schema without reaching for the node
 *   class itself; both `@tiptap/react` (which re-exports `Extension`)
 *   and `@tiptap/pm` (the underlying ProseMirror surface) are direct
 *   deps so the boundary stays clean.
 */

import type { NodeType, Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state'
import { Extension } from '@tiptap/react'

const hash32 = (input: string, seed: number): number => {
  let h = seed >>> 0
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h
}

/**
 * Deterministic replacement id for the `ordinal`-th duplicate of `id`
 * (ordinal 1 = the second paragraph carrying it). UUID-shaped so it sits
 * beside minted ids indistinguishably; FNV-1a over four seeds — no async
 * crypto, so it runs inside `appendTransaction`.
 */
export const deriveDuplicateParagraphId = (id: string, ordinal: number): string => {
  const input = `${id}#${ordinal}`
  const hex = [0x811c9dc5, 0x01000193, 0x9e3779b9, 0x7f4a7c15]
    .map((seed) => hash32(input, seed).toString(16).padStart(8, '0'))
    .join('')
  // Stamp the v4 version nibble + RFC 4122 variant so the shape is a UUID.
  const chars = hex.split('')
  chars[12] = '4'
  chars[16] = '8'
  const h = chars.join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`
}

export interface ParagraphIdFix {
  pos: number
  type: NodeType
  attrs: Record<string, unknown>
  id: string
}

/**
 * Every paragraph whose id repeats an earlier paragraph's, with the id it
 * should carry instead. Pure — the plugin applies the result, tests read it.
 */
export const findDuplicateParagraphIds = (doc: ProseMirrorNode): ParagraphIdFix[] => {
  const seen = new Set<string>()
  const ordinalById = new Map<string, number>()
  const fixes: ParagraphIdFix[] = []
  doc.descendants((node, pos) => {
    if (node.type.name !== 'paragraph') return undefined
    const id = node.attrs.id
    if (typeof id !== 'string' || id.length === 0) return false
    if (!seen.has(id)) {
      seen.add(id)
      return false
    }
    let ordinal = (ordinalById.get(id) ?? 0) + 1
    let next = deriveDuplicateParagraphId(id, ordinal)
    while (seen.has(next)) {
      ordinal += 1
      next = deriveDuplicateParagraphId(id, ordinal)
    }
    ordinalById.set(id, ordinal)
    seen.add(next)
    fixes.push({ pos, type: node.type, attrs: node.attrs, id: next })
    return false
  })
  return fixes
}

const dedupeTransaction = (tr: Transaction, doc: ProseMirrorNode): Transaction | null => {
  const fixes = findDuplicateParagraphIds(doc)
  if (fixes.length === 0) return null
  for (const fix of fixes) tr.setNodeMarkup(fix.pos, undefined, { ...fix.attrs, id: fix.id })
  // Never a user-visible edit: out of the undo stack, like the back-fill.
  tr.setMeta('addToHistory', false)
  return tr
}

export const paragraphIdPluginKey = new PluginKey('paragraphIdDedupe')

export const ParagraphIdExtension = Extension.create({
  name: 'paragraphId',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: paragraphIdPluginKey,
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null
          return dedupeTransaction(newState.tr, newState.doc)
        },
        // Repair a doc that ALREADY holds duplicates when it loads (no edit
        // may ever happen in view mode, and a viewer anchors comments too).
        view: (editorView) => {
          queueMicrotask(() => {
            if (editorView.isDestroyed) return
            const tr = dedupeTransaction(editorView.state.tr, editorView.state.doc)
            if (tr) editorView.dispatch(tr)
          })
          return {}
        },
      }),
    ]
  },

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          id: {
            default: null,
            parseHTML: (element) => element.getAttribute('data-paragraph-id'),
            renderHTML: (attributes) => {
              if (!attributes.id) return {}
              return { 'data-paragraph-id': attributes.id }
            },
          },
        },
      },
    ]
  },
})
